import { useEffect, useMemo, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  deleteChatHistory,
  deleteChatHistoryBulk,
  exportHistoryCsv,
  exportHistoryJson,
  subscribeChatHistory,
} from '@/services/supportService';
import type { ChatHistoryRecord } from '@/types/support';

function download(filename: string, content: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function ChatHistoryPage() {
  const [rows, setRows] = useState<ChatHistoryRecord[]>([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<ChatHistoryRecord | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    return subscribeChatHistory(
      (list) => {
        setRows(list);
        setLoading(false);
      },
      (message) => {
        setError(message);
        setLoading(false);
      },
    );
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.agentName.toLowerCase().includes(q) ||
        (r.customerName || '').toLowerCase().includes(q) ||
        r.issueSummary.toLowerCase().includes(q) ||
        r.requestId.toLowerCase().includes(q),
    );
  }, [rows, search]);

  const toggle = (id: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (checked.size === filtered.length) setChecked(new Set());
    else setChecked(new Set(filtered.map((r) => r.requestId)));
  };

  const confirmDelete = async (ids: string[]) => {
    if (ids.length === 0) return;
    const ok = window.confirm(
      ids.length === 1
        ? 'Delete this conversation from Firebase? This cannot be undone.'
        : `Delete ${ids.length} conversations from Firebase? This cannot be undone.`,
    );
    if (!ok) return;
    setDeleting(true);
    setError(null);
    try {
      if (ids.length === 1) await deleteChatHistory(ids[0]!);
      else await deleteChatHistoryBulk(ids);
      setChecked((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
      if (selected && ids.includes(selected.requestId)) setSelected(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-5 pb-24 md:pb-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Chat history</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Closed conversations. Delete removes data from Realtime Database.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 sm:flex-none"
              onClick={() => download('chat-history.json', exportHistoryJson(filtered), 'application/json')}
            >
              Export JSON
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1 sm:flex-none"
              onClick={() => download('chat-history.csv', exportHistoryCsv(filtered), 'text/csv')}
            >
              Export CSV
            </Button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <Input
            className="sm:max-w-md"
            placeholder="Search agent, customer, issue, ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {checked.size > 0 && (
            <Button
              size="sm"
              variant="destructive"
              disabled={deleting}
              className="gap-1.5 shrink-0"
              onClick={() => void confirmDelete([...checked])}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete ({checked.size})
            </Button>
          )}
        </div>

        {error && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 text-destructive text-sm p-3">
            {error}
            <p className="text-xs mt-1 opacity-80">
              If this is a permission error, re-publish `firebase/database.rules.json` in Firebase Console.
            </p>
          </div>
        )}

        {loading && (
          <p className="text-sm text-muted-foreground">Loading history…</p>
        )}

        {/* Mobile cards */}
        <div className="md:hidden space-y-2">
          {filtered.length === 0 && !loading && (
            <div className="rounded-xl border border-border p-8 text-center text-sm text-muted-foreground">
              No history yet. Close a conversation from the Inbox to save it here.
            </div>
          )}
          {filtered.map((r) => (
            <div key={r.requestId} className="rounded-xl border border-border p-3 space-y-2 bg-background">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={checked.has(r.requestId)}
                  onChange={() => toggle(r.requestId)}
                  aria-label={`Select ${r.requestId}`}
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{r.customerName || 'Anonymous'}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {r.agentName} · {new Date(r.endedAt).toLocaleString()}
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{r.issueSummary}</p>
                </div>
              </div>
              <div className="flex gap-2 pl-7">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => setSelected(r)}>
                  View
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={deleting}
                  onClick={() => void confirmDelete([r.requestId])}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <th className="p-3 w-10">
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && checked.size === filtered.length}
                    onChange={toggleAll}
                    aria-label="Select all"
                  />
                </th>
                <th className="p-3 font-medium">Ended</th>
                <th className="p-3 font-medium">Customer</th>
                <th className="p-3 font-medium">Agent</th>
                <th className="p-3 font-medium">Duration</th>
                <th className="p-3 font-medium">Issue</th>
                <th className="p-3 font-medium" />
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && !loading && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-muted-foreground">
                    No history yet. Close a conversation from the Inbox to save it here.
                  </td>
                </tr>
              )}
              {filtered.map((r) => (
                <tr key={r.requestId} className="border-t border-border">
                  <td className="p-3">
                    <input
                      type="checkbox"
                      checked={checked.has(r.requestId)}
                      onChange={() => toggle(r.requestId)}
                      aria-label={`Select ${r.requestId}`}
                    />
                  </td>
                  <td className="p-3 whitespace-nowrap">{new Date(r.endedAt).toLocaleString()}</td>
                  <td className="p-3">{r.customerName || 'Anonymous'}</td>
                  <td className="p-3">{r.agentName}</td>
                  <td className="p-3">{Math.round(r.durationSec / 60)}m {r.durationSec % 60}s</td>
                  <td className="p-3 max-w-xs truncate">{r.issueSummary}</td>
                  <td className="p-3">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => setSelected(r)}>View</Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        disabled={deleting}
                        onClick={() => void confirmDelete([r.requestId])}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-background rounded-t-2xl sm:rounded-xl border border-border w-full max-w-lg max-h-[85dvh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            <div className="p-4 border-b border-border flex items-center justify-between gap-2">
              <div className="min-w-0">
                <h2 className="font-semibold text-sm">Transcript</h2>
                <p className="text-xs text-muted-foreground truncate">{selected.requestId}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={deleting}
                  onClick={() => void confirmDelete([selected.requestId])}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSelected(null)}>Close</Button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2 text-sm">
              {selected.messages.map((m) => (
                <div key={m.messageId} className="rounded-md bg-muted/50 px-3 py-2">
                  <div className="text-[10px] uppercase text-muted-foreground mb-0.5">
                    {m.senderRole} · {new Date(m.createdAt).toLocaleString()}
                  </div>
                  {m.text}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
